import './styles.css';
import './minigames/games/sports';
import './minigames/games/arcade';
import './minigames/games/market';
import './minigames/games/trips';
import './content/pkgSky';
import './content/pkgFamily';
import './content/pkgClassroom';
import './content/pkgLife';
import './content/pkgXinyi';
import './content/pkgXimen';
import './content/pkgCks';
import './content/pkgHlcity';
import './content/pkgTaroko';
import './content/pkgAirport';
import './content/pkgKaohsiung';
import './content/pkgTainan';
import './content/pkgBarcelona';
import { openArcade } from './ui/arcade';
import { openTreasure } from './ui/treasure';
import { openPassport } from './ui/passport';
import { openTutorial, tutorialSeen } from './ui/tutorial';
import { initCheats } from './ui/cheats';
import { m, getLang, onLangChange, setLang } from './i18n';
import { createState, DEFAULT_BIRTHDAYS } from './core/state';
import { hasGame, S, setGame } from './core/game';
import { clearSave, loadGame, saveGame } from './core/save';
import { unlockAudio } from './audio/sfx';
import { World } from './world/world';
import { OBJECTS } from './world/map';
import { NPCS } from './world/npcs';
import { chatWith } from './content/conversations';
import { bindBusWorld } from './content/bus';
import { doorClosed, enteredMap } from './content/world3';
import { bindTripsWorld, enteredTrip, tripTick } from './content/trips';
import { doorBlocked } from './content/registry';
import { todaysSpots } from './content/treasure';
import { input, MOVE_CODES } from './world/input';
import { Renderer } from './render/renderer';
import { interact } from './content/interactions';
import { bindWorld, needsTick, resumeDay, startDay } from './content/dayCycle';
import { callsTick, initCalls } from './content/calls';
import { openPartyMenu } from './content/party';
import { openWallet } from './ui/wallet';
import { openJournal as openGrowthJournal } from './ui/journal';
import { $ } from './ui/dom';
import { layers } from './ui/layers';
import { initToasts } from './ui/toast';
import { closeDialog, dialogKey, dialogOpen } from './ui/dialog';
import { closeTopPanel } from './ui/panel';
import { armPlaceCard, initHud, hudTick, update as updateHud } from './ui/hud';
import { hideStart, showStart } from './ui/start';
import { openMenu } from './ui/menu';
import { openBag } from './ui/inventory';
import { openQuestbook } from './ui/quests';
import { openMap } from './ui/minimap';
import { guideTarget, markers, questTick } from './content/guide';
import { toast } from './core/effects';
import { dayId } from './core/state';
import { openEnglishHere, setEnglishArea } from './ui/english';
import { blocksKey } from './ui/blocks';
import { closePhone, initPhone, openPhone, phoneOpen, registerApps, unreadCount } from './phone/phone';
import { initChats } from './phone/chats';
import { initCallsTab } from './phone/calls';
import { initFeed } from './phone/feed';
import { initRadio } from './phone/radio';
import { say } from './ui/dialog';

// A demo state so the world renders behind the start screen.
setGame(createState({ look: 0, pet: 'rabbit', birthdays: DEFAULT_BIRTHDAYS }));
S().time = 8 * 60 + 30;

const canvas = $('cv') as HTMLCanvasElement;
const renderer = new Renderer(canvas);
const world: World = new World({
  interact: (t) => interact(t),
  tick: (dt) => {
    needsTick();
    callsTick(dt);
    questTick(dt, world);
    tripTick(world);
  },
  // The school is closed at the weekend.
  canUse: (door) => {
    if (door.to === 'school' && dayId(S()) !== 'fri') {
      toast(`🔒 ${m().common.schoolClosed}`);
      return false;
    }
    const closed = doorBlocked(door.to, world.map.id) ?? (world.map.id === 'town' ? doorClosed(door.to) : null);
    if (closed) {
      toast(`🔒 ${closed}`);
      return false;
    }
    return true;
  },
  mapChanged: () => {
    updateHud();
    renderer.warm(world);
    enteredMap(world.map.id);
    enteredTrip(world.map.id);
  },
});
bindWorld(world);
bindBusWorld(world);
bindTripsWorld(world);
setEnglishArea(() => world.areaKey());

world.teleport('town', 448, 200);
let playing = false;

// --- Layout: logical view sized to the screen (portrait phones see ~360px of
// world across; landscape screens see more), rendered at an integer scale. ----
function layout(): void {
  const wrap = $('wrap');
  const w = wrap.clientWidth, hh = wrap.clientHeight;
  // ~250 logical px across a phone: characters read big and clear.
  const k = Math.max(0.8, Math.min(w / 250, hh / 460));
  world.viewW = Math.round(w / k);
  world.viewH = Math.round(hh / k);
  // 2 device px per logical px is plenty for the art and keeps fill-rate low on 3× phones.
  const scale = Math.min(2, Math.max(1, Math.ceil(k * (window.devicePixelRatio || 1))));
  renderer.resize(world.viewW, world.viewH, scale);
  canvas.style.width = `${w}px`;
  canvas.style.height = `${hh}px`;
  world.snapCamera();
}
window.addEventListener('resize', layout);
layout();

// --- Input ------------------------------------------------------------------
function toWorld(e: PointerEvent): { x: number; y: number } {
  const r = canvas.getBoundingClientRect();
  return { x: ((e.clientX - r.left) / r.width) * world.viewW + world.camX, y: ((e.clientY - r.top) / r.height) * world.viewH + world.camY };
}

let dragging = false;
let lastSteer = 0;
canvas.addEventListener('pointerdown', (e) => {
  unlockAudio();
  if (!playing || layers.blocking()) return;
  dragging = true;
  canvas.setPointerCapture(e.pointerId);
  const p = toWorld(e);
  world.tapAt(p.x, p.y);
});
canvas.addEventListener('pointermove', (e) => {
  if (!dragging || layers.blocking()) return;
  const now = performance.now();
  if (now - lastSteer < 120) return;
  lastSteer = now;
  const p = toWorld(e);
  world.steer(p.x, p.y);
});
const endDrag = () => (dragging = false);
canvas.addEventListener('pointerup', endDrag);
canvas.addEventListener('pointercancel', endDrag);

document.querySelectorAll<HTMLButtonElement>('#dpad button[data-d]').forEach((btn) => {
  const d = btn.dataset.d as 'up' | 'down' | 'left' | 'right';
  const on = (e: PointerEvent) => {
    e.preventDefault();
    unlockAudio();
    btn.setPointerCapture(e.pointerId);
    input.setPad(d, true);
    world.cancelMove();
    btn.classList.add('active');
  };
  const off = () => {
    input.setPad(d, false);
    btn.classList.remove('active');
  };
  btn.addEventListener('pointerdown', on);
  btn.addEventListener('pointerup', off);
  btn.addEventListener('pointercancel', off);
  btn.addEventListener('lostpointercapture', off);
});

window.addEventListener('keydown', (e) => {
  // Typing in any text field (chat, comments, posts) must never move the player.
  const el = e.target as HTMLElement | null;
  if (el && (el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement || el.isContentEditable)) return;
  unlockAudio();
  if (dialogKey(e)) return e.preventDefault();
  if (blocksKey(e)) return;
  if (e.key === 'Escape') {
    if (phoneOpen()) return closePhone();
    if (closeTopPanel()) return;
    if (playing && !layers.blocking()) openMenu(newGame);
    return;
  }
  if (!playing || layers.blocking()) return;
  if (MOVE_CODES.has(e.code)) {
    input.keyDown(e.code);
    e.preventDefault();
    return;
  }
  if (e.repeat) return;
  switch (e.code) {
    case 'Space':
      // Space uses what's in front of you, and jumps when there's nothing there.
      e.preventDefault();
      if (world.near) world.interact();
      else world.jump();
      break;
    case 'Enter':
    case 'KeyE':
      e.preventDefault();
      world.interact();
      break;
    case 'KeyX':
    case 'ShiftLeft':
    case 'ShiftRight':
      world.jump();
      break;
    case 'KeyB':
      openBag();
      break;
    case 'KeyT':
      openQuestbook('today');
      break;
    case 'KeyP':
      openPhone();
      break;
    case 'KeyM':
      openMap(world, () => guideTarget(world));
      break;
    case 'KeyQ':
    case 'KeyJ':
      openQuestbook('quests');
      break;
  }
});
window.addEventListener('keyup', (e) => input.keyUp(e.code));
window.addEventListener('blur', () => input.reset());
// Opening any UI layer drops held movement so the player never "sticks" walking.
layers.onChange(() => {
  if (layers.blocking()) {
    input.reset();
    document.querySelectorAll('#dpad .active').forEach((b) => b.classList.remove('active'));
  }
});

// --- Buttons ----------------------------------------------------------------
const bind = (id: string, fn: () => void) =>
  $(id).addEventListener('click', () => {
    unlockAudio();
    if (!playing) return;
    if (dialogOpen()) closeDialog();
    fn();
  });
bind('ctxBtn', () => (world.near ? world.interact() : say({ who: m().common.thoughts, icon: '💭', text: m().common.breathe })));
$('jumpBtn').addEventListener('pointerdown', (e) => {
  e.preventDefault();
  unlockAudio();
  if (playing && !layers.blocking()) world.jump();
});
bind('timebox', () => openQuestbook('today'));
bind('taskBar', () => openQuestbook());
bind('btnJournal', () => openQuestbook());
bind('btnBag', openBag);
bind('btnMap', () => openMap(world, () => guideTarget(world)));
bind('btnPhone', () => openPhone());
bind('btnMenu', () => openMenu(newGame));

// Every tool lives on the phone's home screen, like an app.
const app = (id: string, icon: string, label: () => string, colors: [string, string], run: () => void, dock = false) => ({
  id, icon, label, colors, dock,
  run: () => {
    closePhone();
    run();
  },
});
const strip = (s: string) => s.replace(/^\S+\s/, '');
registerApps([
  { id: 'chats', icon: '💬', label: () => strip(m().phone.tabs.chats), colors: ['#3fbf8f', '#1d7a58'], run: () => openPhone('chats'), badge: unreadCount, dock: true },
  { id: 'calls', icon: '📞', label: () => strip(m().phone.tabs.calls), colors: ['#5ec8ff', '#2a6fb0'], run: () => openPhone('calls'), dock: true },
  { id: 'feed', icon: '🌐', label: () => m().tools.social, colors: ['#4a6fe0', '#2e4cb0'], run: () => openPhone('feed'), dock: true },
  { id: 'radio', icon: '📻', label: () => strip(m().phone.tabs.radio), colors: ['#e0533d', '#a8261c'], run: () => openPhone('radio'), dock: true },
  app('english', '🎤', () => m().tools.english, ['#f7a03a', '#c45c0a'], openEnglishHere),
  app('passport', '🛂', () => m().passport.tool, ['#2e9a6a', '#1a4a36'], openPassport),
  app('treasure', '🏴‍☠️', () => strip(m().treasure.title), ['#e0b23a', '#8a5a10'], openTreasure),
  app('arcade', '🕹️', () => strip(m().arcade.title), ['#17b8a0', '#0a7a67'], openArcade),
  app('party', '🎂', () => m().tools.party, ['#f06ab0', '#b8236a'], openPartyMenu),
  app('art', '🎨', () => m().tools.art, ['#b06ad8', '#6d3a8c'], () => interact({ kind: 'object', obj: { id: 'easel', map: 'school', rect: { x: 0, y: 0, w: 0, h: 0 }, solid: false, label: 'easel', icon: '🎨' } })),
  app('wallet', '👛', () => strip(m().life.wallet.menu), ['#f7b733', '#b7791a'], openWallet),
  app('journal', '📔', () => strip(m().classroom.ui.journalMenu), ['#8a6cf0', '#4b3a9c'], openGrowthJournal),
]);

// --- Game flow ----------------------------------------------------------------
function begin(): void {
  playing = true;
  hideStart();
  armPlaceCard(true);
  updateHud();
}

function newGame(): void {
  clearSave();
  playing = false;
  armPlaceCard(false);
  showStart({ onNew, onContinue });
}

function onNew(c: { look: number; pet: Parameters<typeof createState>[0]['pet']; birthdays: Parameters<typeof createState>[0]['birthdays'] }): void {
  setGame(createState(c));
  startDay();
  begin();
  if (!tutorialSeen()) window.setTimeout(openTutorial, 900);
}

function onContinue(): void {
  const s = loadGame();
  if (!s) return;
  setGame(s);
  resumeDay();
  begin();
}

function persist(): void {
  if (playing && hasGame()) {
    S().player.x = world.player.x;
    S().player.y = world.player.y;
    saveGame(S());
  }
}
window.setInterval(persist, 15000);
document.addEventListener('visibilitychange', () => document.hidden && persist());
window.addEventListener('pagehide', persist);

// --- Main loop: variable render, clamped simulation step. ---------------------
let last = performance.now();
let hudTimer = 0;
function frame(now: number): void {
  const dt = Math.min(0.1, (now - last) / 1000);
  last = now;
  if (playing && !layers.blocking()) world.update(dt);
  else world.idle(dt);
  renderer.draw(world, { guide: playing ? guideTarget(world) : null, markers: playing ? markers(world) : [] });
  if ((hudTimer += dt) > 0.2) {
    hudTimer = 0;
    hudTick();
  }
  requestAnimationFrame(frame);

// Installable + offline in production builds (the dev server stays cache-free).
if (import.meta.env.PROD && 'serviceWorker' in navigator) {
  window.addEventListener('load', () => void navigator.serviceWorker.register('./sw.js').catch(() => {}));
}
}

// --- Boot ---------------------------------------------------------------------
document.documentElement.lang = getLang();
document.title = m().meta.title;
onLangChange(() => updateHud());
initToasts();
initHud(world);
initCalls();
initPhone();
initChats();
initCallsTab();
initFeed();
initRadio();
showStart({ onNew, onContinue });

// Reveal the page only once styles are in and the first frame is drawn (no unstyled flash).
requestAnimationFrame(() =>
  requestAnimationFrame(() => {
    document.documentElement.classList.add('ready');
    const boot = document.getElementById('boot');
    boot?.classList.add('out');
    window.setTimeout(() => boot?.remove(), 500);
  }),
);
requestAnimationFrame(frame);

// Debug handle for automated smoke tests (dev server or ?debug only).
if (import.meta.env.DEV || location.search.includes('debug')) {
  if (location.search.includes('debug')) initCheats(world);
  (window as unknown as { __debug: unknown }).__debug = {
    S,
    world,
    startDay,
    objects: OBJECTS,
    talk: (id: string) => interact({ kind: 'npc', npc: NPCS.find((n) => n.id === id)! }),
    chat: (id: string) => chatWith(id),
    spots: () => todaysSpots(),
    use: (id: string) => interact({ kind: 'object', obj: OBJECTS.find((o) => o.id === id)! }), setLang: (l: string) => setLang(l as never) };
}
